import { tenantType, userType } from '@atlassiansox/analytics-web-client/analytics-web-types';
import type AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';
import { useTenantData } from '@dev-agents/tenant-context';
import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';

import { devAIAnalyticsClient, devAnalyticsClient } from './client';

interface AuthData {
	user?: {
		accountId: string;
	};
}

interface AnalyticsWebClientProviderProps {
	children: React.ReactNode;
	authData: AuthData;
}

const AnalyticsWebClientContext = createContext<AnalyticsWebClient | undefined>(undefined);
const AIAnalyticsWebClientContext = createContext<AnalyticsWebClient | undefined>(undefined);

export const AnalyticsWebClientProvider = ({
	children,
	authData,
}: AnalyticsWebClientProviderProps) => {
	const { tenantContexts } = useTenantData();
	const accountId = authData.user?.accountId;
	const cloudId = tenantContexts[0]?.cloudId;
	const hasStartedUIViewedEvent = useRef(false);

	const analyticsWebClient = useMemo(() => {
		if (accountId && cloudId) {
			devAnalyticsClient.setTenantInfo(tenantType.CLOUD_ID, cloudId);
			devAnalyticsClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, accountId);
		}

		return devAnalyticsClient;
	}, [accountId, cloudId]);

	useEffect(() => {
		if (!accountId || !cloudId || hasStartedUIViewedEvent.current) {
			return;
		}

		devAnalyticsClient.startUIViewedEvent();
		hasStartedUIViewedEvent.current = true;
	}, [accountId, cloudId]);

	// Mirror tenant + user identity onto the AI client so AI events carry
	// the same envelope as standard track events. `startUIViewedEvent()` is
	// intentionally not called here — it's a product-level concern on the
	// base client only.
	const aiAnalyticsWebClient = useMemo(() => {
		if (accountId && cloudId) {
			devAIAnalyticsClient.setTenantInfo(tenantType.CLOUD_ID, cloudId);
			devAIAnalyticsClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, accountId);
		}
		return devAIAnalyticsClient;
	}, [accountId, cloudId]);

	return (
		<AnalyticsWebClientContext.Provider value={analyticsWebClient}>
			<AIAnalyticsWebClientContext.Provider value={aiAnalyticsWebClient}>
				{children}
			</AIAnalyticsWebClientContext.Provider>
		</AnalyticsWebClientContext.Provider>
	);
};

export const useAnalyticsWebClient = (): AnalyticsWebClient => {
	const analyticsWebClient = useContext(AnalyticsWebClientContext);

	if (!analyticsWebClient) {
		throw new Error('Must be used inside AnalyticsWebClientProvider');
	}

	return analyticsWebClient;
};

export const useAIAnalyticsWebClient = (): AnalyticsWebClient => {
	const aiAnalyticsWebClient = useContext(AIAnalyticsWebClientContext);

	if (!aiAnalyticsWebClient) {
		throw new Error('Must be used inside AnalyticsWebClientProvider');
	}

	return aiAnalyticsWebClient;
};
